import { ApiClient } from '@/config/axios.config';
import { environment } from '@/config/environment';
import type {
  FileReport,
  FileReportCategoryOption,
  FileReportFormValues,
  FileReportsResponse,
  FileReportsFilters,
  FileReportsPagination,
} from '@/types/reports.types';

/**
 * ApiClient sends `Content-Type: application/json` by default, and axios turns a FormData body
 * into JSON under that header, silently dropping the file. Naming multipart makes axios pass the
 * FormData through so the browser sets the boundary. Uploads (up to 25 MB) also get longer than
 * the default 30 s timeout.
 */
const UPLOAD_CONFIG = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 120000,
};

export class ReportsService {
  /**
   * Fetch file reports with pagination and filters.
   * `admin` reads `/reports/files/manage` (REPORTS_FILES_ADMIN): no pool or date filter, page ids
   * for editing, and hidden files when `filters.includeHidden` is set.
   */
  static async getFileReports(
    pagination: FileReportsPagination,
    filters?: FileReportsFilters,
    { admin = false }: { admin?: boolean } = {}
  ): Promise<FileReportsResponse> {
    try {
      const params = new URLSearchParams({
        pageSize: pagination.pageSize.toString(),
        pageNumber: pagination.pageNumber.toString(),
        dateOrder: pagination.dateOrder,
      });

      // Add filter parameters if provided
      if (filters?.search) {
        params.append('filterName', filters.search);
      }
      if (filters?.category) {
        params.append('filterCategory', filters.category);
      }
      if (filters?.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters?.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      if (admin && filters?.includeHidden) {
        params.append('includeHidden', 'true');
      }

      const url = `${admin ? '/reports/files/manage' : '/reports/files'}?${params.toString()}`;
      const data = await ApiClient.get<unknown>(url);

      // Transform the response to match our expected format
      return this.transformResponse(data, pagination);
    } catch (error) {
      console.error('Failed to fetch file reports:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch file reports');
    }
  }

  /**
   * Get unique categories from the API
   */
  static async getCategories(): Promise<string[]> {
    try {
      const url = '/reports/files/categories';
      const categories = await ApiClient.get<string[] | Array<{ categoryName: string }>>(url);

      // Handle both string[] and object[] formats from API
      if (Array.isArray(categories)) {
        return categories.map((cat) =>
          typeof cat === 'string' ? cat : (cat as { categoryName: string }).categoryName
        );
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Return default categories as fallback
      if (!environment.production) {
        return ['Daily Reports', 'Financial Reports', 'Counterparty Report', 'Key Entitlement'];
      }

      throw new Error(error instanceof Error ? error.message : 'Failed to fetch categories');
    }
  }

  /**
   * Every active category the admin form can file under, including ones with no files yet
   */
  static async getCategoryOptions(): Promise<FileReportCategoryOption[]> {
    return ApiClient.get<FileReportCategoryOption[]>('/reports/files/category-options');
  }

  // The write methods below let errors through untouched so callers can show the API's message
  // (409 caption in use, 400 validation, 413 too large) via getWriteErrorMessage.

  static async createFileReport(
    values: FileReportFormValues,
    file: File
  ): Promise<{ pageId: number; fileId: number }> {
    const form = new FormData();
    form.append('pageCaption', values.pageCaption);
    form.append('categoryId', values.categoryId.toString());
    form.append('pageDate', values.pageDate);
    form.append('pagePriority', values.pagePriority.toString());
    form.append('reportAnnotation', values.reportAnnotation);
    form.append('visible', values.visible.toString());
    form.append('file', file);
    return ApiClient.post('/reports/files', form, UPLOAD_CONFIG);
  }

  static async updateFileReport(pageId: number, values: FileReportFormValues): Promise<void> {
    await ApiClient.put(`/reports/files/pages/${pageId}`, values);
  }

  /** Stores the file as a new file row, so the report's fileId changes. */
  static async replaceFile(
    pageId: number,
    file: File
  ): Promise<{ pageId: number; fileId: number }> {
    const form = new FormData();
    form.append('file', file);
    return ApiClient.put(`/reports/files/pages/${pageId}/file`, form, UPLOAD_CONFIG);
  }

  static async setVisibility(pageId: number, visible: boolean): Promise<void> {
    await ApiClient.put(`/reports/files/pages/${pageId}/visibility`, { visible });
  }

  static async deleteFileReport(pageId: number): Promise<void> {
    await ApiClient.delete(`/reports/files/pages/${pageId}`);
  }

  /**
   * Download a file by ID
   */
  static async downloadFile(fileId: number, fileName: string): Promise<void> {
    try {
      const url = `/reports/files/${fileId}`;
      const { data: blob, headers } = await ApiClient.downloadWithHeaders(url);

      const contentDisposition = headers['content-disposition'] || headers['Content-Disposition'];
      const headerFileName = contentDisposition?.split('filename="')[1]?.split('"')[0]?.trim();
      const resolvedFileName = headerFileName || fileName;

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to download file:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to download file');
    }
  }

  /**
   * Transform API response to match our expected format
   */
  private static transformResponse(
    data: unknown,
    pagination: FileReportsPagination
  ): FileReportsResponse {
    // Handle different possible response formats
    let files: FileReport[] = [];
    let totalCount = 0;
    let hasNextPage = false;

    if (Array.isArray(data)) {
      // Simple array response
      files = data;
      totalCount = data.length;
      hasNextPage = data.length === pagination.pageSize;
    } else if (data && typeof data === 'object') {
      // Object response with pagination info
      const dataObj = data as Record<string, unknown>;
      files = (dataObj.data || dataObj.files || dataObj.items || []) as FileReport[];
      totalCount = (dataObj.totalCount || dataObj.total || files.length) as number;
      hasNextPage = (dataObj.hasNextPage as boolean) ?? files.length === pagination.pageSize;
    }

    return {
      data: files,
      totalCount,
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      hasNextPage,
      hasPreviousPage: pagination.pageNumber > 1,
    };
  }
}
